import type { PrototypeVersion, UploadVersionResult } from '@atlas/api-contract';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/errors';
import {
  getVersion,
  listVersions,
  uploadVersion,
  type PrototypeVersionSummary,
  type UploadProgress,
} from '@/lib/api/versions';
import { queryKeys } from '@/lib/query/keys';

/**
 * Every version of one prototype, newest first — the version drawer's list.
 *
 * Not paginated and not filtered, because the drawer shows all of them and the
 * endpoint returns all of them; a prototype accumulates versions at the speed a
 * human uploads ZIPs, so there is nothing here worth paging.
 */
export const versionsQueryOptions = (prototypeId: string) =>
  queryOptions<PrototypeVersionSummary[], ApiError>({
    queryKey: queryKeys.versions.list(prototypeId),
    queryFn: ({ signal }) => listVersions(prototypeId, { signal }),
  });

export const useVersionsQuery = (
  prototypeId: string,
): UseQueryResult<PrototypeVersionSummary[], ApiError> =>
  useQuery(versionsQueryOptions(prototypeId));

/**
 * One version, with the `entryPath` and `screenPaths` the viewer needs.
 *
 * An uploaded version is immutable apart from its notes, so this is as close to
 * static as anything in the app gets — but it is left on the default stale time
 * rather than pinned to `Infinity`, because notes *are* editable and a cache
 * that never refetched would show a stale note forever.
 */
export const versionQueryOptions = (versionId: string) =>
  queryOptions<PrototypeVersion, ApiError>({
    queryKey: queryKeys.versions.detail(versionId),
    queryFn: ({ signal }) => getVersion(versionId, { signal }),
  });

/**
 * `versionId` is nullable because the viewer has none until the version list
 * resolves, and a prototype nobody has uploaded to never gets one. React Query
 * needs a stable key either way, so the query is *disabled* rather than skipped
 * — the caller reads `isPending` the same way in both cases.
 */
export const useVersionQuery = (
  versionId: string | null,
): UseQueryResult<PrototypeVersion, ApiError> =>
  useQuery({
    ...versionQueryOptions(versionId ?? '__none__'),
    enabled: versionId !== null,
  });

/**
 * React Query binding for the one upload in the app.
 *
 * Two things are passed *through* the mutation rather than held by it:
 *
 * - `signal`, because the dialog owns the `AbortController` — the user's cancel
 *   button has to reach this exact request, and a controller created here would
 *   be out of reach of the component that renders the button;
 * - `onProgress`, because progress is per-attempt and belongs to the component
 *   drawing the bar, not to the cache.
 *
 * `projectId` is carried purely for invalidation: the new version changes the
 * prototype's `latestVersionNumber` and `updatedAt`, both of which are rendered
 * on the card in the list the user is looking at.
 */
export interface UploadVersionVariables {
  prototypeId: string;
  /** Carried only so the right prototype list can be invalidated. */
  projectId: string;
  file: File;
  /** Trimmed notes, or `null` when the box was left empty. */
  notes: string | null;
  signal?: AbortSignal | undefined;
  onProgress?: ((progress: UploadProgress) => void) | undefined;
}

export const useUploadVersion = (): UseMutationResult<
  UploadVersionResult,
  ApiError,
  UploadVersionVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<UploadVersionResult, ApiError, UploadVersionVariables>({
    mutationFn: ({ prototypeId, file, notes, signal, onProgress }) =>
      uploadVersion({ prototypeId, file, notes }, { signal, onProgress }),
    onSuccess: (_result, { prototypeId, projectId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.prototypes.lists(projectId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.prototypes.detail(prototypeId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.versions.list(prototypeId),
        }),
      ]),
  });
};
